import { isNil } from 'ramda';
import React, { Component } from 'react';
import Dialog from '.';
import Button from '../Button';
import CheckBox from '../Checkbox';
import Link from '../Link';
import RadioGroup from '../RadioGroup';

export interface IConfirmDialogProps {
  confirm?: boolean | ((event: any) => boolean);
  name?: string;
  confirmTitle?: string;
  confirmText?: string | ((value: any) => React.ReactNode);
  confirmButtonText?: string;
  confirmCancelButtonText?: string;
  confirmDialogWidth?: string | number;
  alwaysEnabled?: boolean;
  value?: any;
}

/**
 * A confirmation dialog to present
 * prior to handling an event
 */
class ConfirmDialog extends Component<React.PropsWithChildren<IConfirmDialogProps>, any, any> {
  // hasConfirmProps(props) enables us to check whether a target props object contains confirm props
  // and is suitable for a confirmDialog
  static confirmProps = [
    'confirm',
    'confirmTitle',
    'confirmText',
    'confirmButtonText',
    'confirmCancelButtonText',
    'confirmDialogWidth',
  ];
  static hasConfirmProps = (props: any): props is IConfirmDialogProps =>
    Object.keys(props).some((k) => ConfirmDialog.confirmProps.includes(k));

  static idCounter = 0;

  constructor(props: IConfirmDialogProps) {
    super(props);
    this.state = {
      dialogVisible: false,
      delayedEvent: null,
      pendingValue: null,
      uncontrolledElementValue: null,
      confirmDialogId: ConfirmDialog.idCounter++,
    };
  }

  closeDialog(save: boolean) {
    if (save) this.state.delayedEvent();
    this.setState({ dialogVisible: false, pendingValue: null, delayedEvent: null });
  }

  injectDelayedEvent(callEvent: any) {
    // This will delay an event-callee if it is pending a confirm.
    const { confirm = true, value } = this.props;
    return (evt: any) => {
      if (
        (confirm === true || (typeof confirm === 'function' && confirm(evt && evt.data))) &&
        // If we're confirming a value change, we only want to confirm when value has actually changed
        // If this isn't a value changing event we always confirm
        ((isNil(value) && isNil(evt.data?.value)) || evt.data?.value !== value)
      ) {
        evt.preventDefault();

        // Get pending value at this point in time
        var pendingValue = evt.data?.value;
        // Save current state before showing dialog otherwise the data value will be lost
        const currentEventState = evt.data
          ? {
              ...evt,
              data: { ...evt.data, value: pendingValue },
            }
          : { ...evt };

        this.setState({ dialogVisible: true, pendingValue, delayedEvent: callEvent.bind(null, currentEventState) });
      } else {
        callEvent(evt);
      }
    };
  }

  eventInterceptedChildren() {
    const child: any = React.Children.only(this.props.children);
    let { onChange, onClick, value } = child.props;
    const props: any = {};
    // Intercept events, and conditionally fire them.
    switch (child.type) {
      case RadioGroup:
      case CheckBox:
        // Uncontrolled elements need to be upgraded to controlled so that we can prevent events on them
        if (value === undefined) {
          props.value = this.state.uncontrolledElementValue;
          const originalOnChange = onChange;
          onChange = (evt: any) => {
            this.setState({ uncontrolledElementValue: evt.data.value });
            if (originalOnChange) originalOnChange(evt);
          };
        }
        props.onChange = this.injectDelayedEvent(onChange);
        break;
      case Link:
      case Button:
        props.onClick = this.injectDelayedEvent(onClick);
        break;
      default:
        throw new Error(`<ConfirmDialog> does not support type '${child.type}'.`);
    }
    return React.cloneElement(child, props);
  }

  render() {
    const { confirmDialogId, pendingValue } = this.state;
    const {
      name = `confirmDialog_${confirmDialogId}`,
      confirmTitle = 'Confirm',
      confirmText = 'Are you sure you want to complete this action?',
      confirmButtonText = 'Yes',
      confirmCancelButtonText = 'Cancel',
      confirmDialogWidth = '400px',
      alwaysEnabled,
    } = this.props;

    return (
      <>
        <Dialog
          name={name}
          title={confirmTitle}
          buttons={[
            <Button
              primary
              key="1"
              autoFocus
              name={`${name}.buttonConfirm`}
              alwaysEnabled={alwaysEnabled}
              onClick={() => this.closeDialog(true)}
            >
              {confirmButtonText}
            </Button>,
            <Button
              key="0"
              name={`${name}.buttonCancel`}
              alwaysEnabled={alwaysEnabled}
              onClick={() => this.closeDialog(false)}
            >
              {confirmCancelButtonText}
            </Button>,
          ]}
          alwaysEnabled={alwaysEnabled}
          visible={this.state.dialogVisible}
          onClose={() => this.closeDialog(false)}
          width={confirmDialogWidth}
        >
          {typeof confirmText === 'function' ? confirmText(pendingValue) : confirmText}
        </Dialog>
        {this.eventInterceptedChildren()}
      </>
    );
  }
}

export { ConfirmDialog };
